import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  SESSION_STORAGE_KEYS,
} from '@panamax/app-state';
import { HelpModalComponent } from '@payments/components/help-modal/help-modal.component';
import { MAX_STATEMENT_SELECTION } from '@payments/constants/constants';
import { HelpTerms } from '@payments/constants/help-for-ar-statements';
import {
  ARStatementsPageViewModel,
  ARStatementsViewModel,
} from '@payments/models/ar-statements-view.model';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { ModalType } from '@payments/services/payments-modal.service';
import { PaymentsService } from '@payments/services/payments.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { ModalService } from '@shared/services/modal/modal.service';
import {
  Filter,
  InvoiceFilterIds,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import { ARDocFormat, ARStatement } from '@usf/payment-types';
import { filter, Observable, skipWhile, Subscription } from 'rxjs';
import {
  ArStatementFilterIds,
  ArStatementSortFields,
  ArStatementsService,
} from './services/ar-statements.service';
import { ARStatementsAnalyticsService } from './tracking/analytics/ar-statements-analytics.service';

@Component({
  selector: 'app-ar-statements',
  templateUrl: './ar-statements.page.html',
  styleUrls: ['./ar-statements.page.scss'],
})
export class ArStatementsPage extends PaymentsBasePage {
  @ViewChild(CdkVirtualScrollViewport)
  virtualScrollViewport: CdkVirtualScrollViewport;

  readonly FilterIds = ArStatementFilterIds;
  readonly HeaderFields = ArStatementSortFields;
  readonly ModalType = ModalType;
  readonly SortDirection = SortDirection;

  vm$: Observable<ARStatementsPageViewModel>;
  arStatementFilters: Filter[];
  filtersAndSort$: Subscription;
  isDesktop: boolean;
  isMobile: boolean;
  isTouch$: Observable<boolean>;
  maxStatementsSelectedMessage: string;
  paymentCustomers$: Subscription;
  sortDirection: SortDirection;
  sortField?: ArStatementSortFields;

  constructor(
    public arStatementsService: ArStatementsService,
    public arAnalyticsService: ARStatementsAnalyticsService,
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    private translateService: TranslateService,
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private modalService: ModalService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter(): void {
    super.ionViewWillEnter();
    this.vm$ = this.arStatementsService.getARStatementsViewModel$();
    this.handleInitialFilters();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.arStatements),
    );
    this.maxStatementsSelectedMessage = this.translateService.instant(
      'i18n.payments.arStatements.maximumStatementsSelected',
      { maximumSelected: MAX_STATEMENT_SELECTION },
    );

    this.filtersAndSort$ = this.arStatementsService
      .getFiltersAndSortInformation$()
      .subscribe(res => {
        this.arStatementFilters = res.filters;
        const sortFilter = res.sort[0] ?? null;
        this.sortDirection = sortFilter?.sortValue || undefined;
        this.sortField =
          sortFilter !== null
            ? ArStatementSortFields[sortFilter.fieldName]
            : undefined;
      });

    let initialLoad = true;
    this.paymentCustomers$ = this.paymentsService
      .getSelectedPaymentCustomers()
      .pipe(
        skipWhile(() => !this.arStatementFilters),
        filter(paymentCustomers => paymentCustomers.customers.length > 0),
      )
      .subscribe(paymentCustomers => {
        if (!initialLoad) {
          let filters = this.arStatementFilters.filter(
            arStatementFilter =>
              arStatementFilter.filterName !== ArStatementFilterIds.customers,
          );
          filters.push({
            filterName: ArStatementFilterIds.customers,
            value: paymentCustomers.customers,
          });
          this.arStatementsService.setFilter(filters);
        }
        initialLoad = false;
      });
    this.setResponsive();
  }

  ionViewDidEnter(): void {
    this.arAnalyticsService.trackPageLoad();
  }

  handleInitialFilters() {
    const queryFilters = this.getFiltersFromParams();
    const sessionFilters: Filter[] = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.paymentsInvoiceFilters),
    );
    const initFilters = queryFilters.filter(
      invoiceFilter => invoiceFilter.filterName !== InvoiceFilterIds.Customers,
    );
    const customerFilter: number[] | string =
      (queryFilters.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      )?.value as number[] | string) ||
      (sessionFilters?.find(
        filter => filter.filterName === InvoiceFilterIds.Customers,
      )?.value as number[]);
    if (customerFilter) {
      initFilters.push({
        filterName: InvoiceFilterIds.Customers,
        value:
          customerFilter === '*'
            ? []
            : (customerFilter as []).map(value => +value),
      });
    }
    this.arStatementsService.setInitialArStatementsSortAndFilter(initFilters);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.paymentsInvoiceFilters);
  }

  ionViewWillLeave() {
    this.filtersAndSort$?.unsubscribe();
    this.paymentCustomers$?.unsubscribe();
  }

  handleSort({
    sortField,
    sortDirection,
  }: {
    sortField: string;
    sortDirection: SortDirection;
  }) {
    this.sort(sortField as ArStatementSortFields, sortDirection);
  }

  sort(sortField: ArStatementSortFields, sortDirection: SortDirection) {
    if (sortField === this.sortField) {
      this.sortDirection =
        this.sortDirection === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortDirection = sortDirection;
    }
    this.sortField = sortField;

    const sortFields: SortFilter[] = [
      { fieldName: sortField, sortValue: this.sortDirection },
    ];
    this.arStatementsService.setSort(sortFields);
  }

  setResponsive(): void {
    this.isTouch$ = this.platformService.isTouchExperience$;
    this.isMobile = this.platformService.platformType === PlatformEnum.mobile;
    this.isDesktop = this.platformService.platformType === PlatformEnum.desktop;
  }

  async openHelpModal() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      HelpModalComponent,
      {
        terms: HelpTerms,
        sectionName: 'i18n.payments.arStatements.title',
      },
      'help-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    await modal.present();
    return await modal.onDidDismiss();
  }

  payNow() {
    this.router.navigate([PATHS.PAYMENTS_MAKE_PAYMENT]);
  }

  clearAllSelections() {
    this.arStatementsService.clearSelectedStatements();
  }

  selectAllStatements(countStatementSelected: number) {
    this.arStatementsService.selectMaxStatements(
      this.maxStatementsSelectedMessage,
      MAX_STATEMENT_SELECTION - countStatementSelected,
    );
  }

  downloadSelectedStatements(arStatementsSelected: ARStatementsViewModel[]) {
    this.downloadStatements(
      arStatementsSelected
        .filter(statement => statement.selected)
        .map(statement => statement.statementID),
    );
  }

  downloadStatements(statementIDs: string[]) {
    this.arStatementsService.showDownloadFormatModal(statementIDs);
  }

  selectArStatement(arStatement: ARStatement) {
    this.arStatementsService.selectStatement(
      arStatement.statementID,
      this.maxStatementsSelectedMessage,
    );
  }

  async openMoreOptions({
    event,
    arStatement,
  }: {
    event: Event;
    arStatement: ARStatement;
  }) {
    const buttons: any[] = [];

    buttons.push(
      {
        text: this.translateService.instant('i18n.common.view'),
        handler: () => {
          this.arStatementsService.downloadStatements(
            [arStatement.statementID],
            ARDocFormat.PDF,
            true,
          );
          this.dismissPopover();
        },
      },
      {
        text: this.translateService.instant('i18n.common.download'),
        handler: () => {
          this.downloadStatements([arStatement.statementID]);
          this.dismissPopover();
        },
      },
    );
    if (!this.platformService.isTouch.value) {
      buttons.push({
        text: this.translateService.instant('i18n.common.print'),
        handler: () => {
          this.arStatementsService.downloadStatements(
            [arStatement.statementID],
            ARDocFormat.PDF,
            true,
          );
          this.dismissPopover();
        },
      });
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.share'),
        handler: () => {
          this.arStatementsService.shareStatement(arStatement.statementID);
          this.dismissPopover();
        },
      });
    }

    if (!this.platformService.isTouch.value) {
      const popover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        cssClass: 'ellipsis-options-popover-desktop',
        componentProps: {
          buttons,
        },
        mode: 'ios',
        event: event,
        showBackdrop: false,
      });
      return await popover.present();
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      });

      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons: buttons,
      });
      await actionSheet.present();
    }
  }

  async dismissPopover() {
    if (!this.platformService.isTouch.value) {
      await this.popoverController.dismiss();
    }
  }

  scrolledIndexChange(_event, pageNum: number, hasNextPage: boolean) {
    const end = this.virtualScrollViewport.getRenderedRange().end;
    const total = this.virtualScrollViewport.getDataLength();
    const nextPage = pageNum + 1;
    if (end === total && hasNextPage) {
      this.arStatementsService.fetchStatements(nextPage);
    }
  }

  trackByIdx(i) {
    return i;
  }
}
